import { Fragment } from 'react'

import { Typography } from '@gto/ui/typography'

import { InlineHighlightedText } from './inline-highlighted-text'

type DashboardSlideTextProps = {
  text: string | string[]
}

// Single-string slide copy marks its line breaks with a literal `<br />`. Split on the
// marker and emit real elements rather than parsing the copy as HTML.
const LINE_BREAK_MARKER = /<br\s*\/?>/

/**
 * The chevron bullet, drawn inline per row rather than through a `<use>` into
 * `<defs>` owned by an ancestor. The indirection was invisible until the admin
 * preview rendered a slide outside the carousel and the bullets came out blank
 * (C-1840): a `<use>` resolves against the whole document, so the marker only
 * appeared where some ancestor happened to have emitted matching ids. Inlining
 * the two paths costs a few bytes per bullet and makes the slide self-contained
 * wherever it is rendered — the app, the preview, or Storybook.
 */
const BulletIcon = () => (
  <svg
    height="10"
    style={{ flexShrink: 0 }}
    viewBox="0 0 10 10"
    width="10"
  >
    <path
      d="M0 0V2.25728L2.74272 5L0 7.74272V10L5 5L0 0Z"
      fill="#69926D"
    />
    <path
      d="M5 0V2.25728L7.74272 5L5 7.74272V10L10 5L5 0Z"
      fill="#AAFBB2"
    />
  </svg>
)

export const DashboardSlideText = ({ text }: DashboardSlideTextProps) => {
  if (Array.isArray(text)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {text.map((item, index) => (
          <div
            // eslint-disable-next-line @eslint-react/no-array-index-key -- static bullet strings from fixed slide config, never reordered
            key={index}
            style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
          >
            <BulletIcon />
            <span style={{ color: '#fff', fontSize: '1.1rem', lineHeight: '1.75rem' }}>
              <InlineHighlightedText text={item} />
            </span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <Typography variant="body1">
      {text.split(LINE_BREAK_MARKER).map((line, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key -- split-once copy, never reordered; keying by content collides on repeated lines (a blank line between paragraphs yields two `''` segments)
        <Fragment key={index}>
          {index > 0 ? <br /> : null}
          <InlineHighlightedText text={line} />
        </Fragment>
      ))}
    </Typography>
  )
}
